import React, { useCallback, useEffect, useState } from 'react';
import { authApiClient } from '../../services/authApi';
import { cronBFamilyLabel } from './CronASummaryTable';

// Cron B (DB-store reconciliation) — one tab per pipeline job. Reads only the
// prod reconciliation_runs rows the jobs write (build rows arrive via the merge
// job's buffer→prod sync, hence "synced at").
export type CronBStage = 'export' | 'build' | 'merge' | 'revert';
export const CRON_B_STAGES: { key: CronBStage; label: string; hint: string }[] = [
  { key: 'export', label: 'Export', hint: 'prod: snapshot each doc’s stored quality signal' },
  { key: 'build', label: 'Build', hint: 'backfill VM: re-parse, judge better / same / worse' },
  { key: 'merge', label: 'Merge', hint: 'prod: swap in better parses, hold on any doubt' },
  { key: 'revert', label: 'Revert', hint: 'prod: restore a merged doc from its snapshot' },
];

export interface CronBRouteEntry {
  route?: string;
  counts?: Record<string, number>;
  [key: string]: unknown;
}

export interface CronBRun {
  run_id: string;
  route: string | null;
  job_kind: string;
  status: string;
  mode: string;
  environment: string | null;
  source_db: string | null;
  started_at: string | null;
  completed_at: string | null;
  synced_at: string | null;
  total_selected: number | null;
  total_promoted: number | null;
  total_held: number | null;
  total_equivalent: number | null;
  total_failed: number | null;
  per_route: CronBRouteEntry[] | null;
  reason: string | null;
  // Headline numbers classified by the backend (run_telemetry.figures) — the UI
  // never re-classifies outcome keys itself.
  figures?: CronBFigures;
}

export interface CronBFigures {
  built?: number;
  better?: number;
  same?: number;
  worse?: number;
  held?: number;
  failed?: number;
  merged?: number;
  held_by_reason?: Record<string, number>;
  reverted?: number;
  refused?: number;
}

export interface MergedDocCounts { active: number; reverted: number; superseded: number }

export interface CronBLatestRoute {
  route: string;
  region: string | null;
  scheduled: boolean;
  run: CronBRun | null;
  backlog?: number | null;
  backlog_at?: string | null;
  merged_docs?: MergedDocCounts | null;
}

export interface CronBLatestResponse {
  stage: CronBStage;
  routes: CronBLatestRoute[];
  last_synced_at?: string | null;
  merged_table_missing?: boolean;
}

interface MergedDoc {
  rollback_id: number;
  document_id: number;
  doc_key: Record<string, unknown>;
  status: string;
  merged_at: string | null;
}

const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString());
export const fmtTime = (iso: string | null | undefined) =>
  iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : '—';

const entryOf = (run: CronBRun | null): CronBRouteEntry =>
  (run && Array.isArray(run.per_route) && run.per_route[0]) || {};
const countsOf = (run: CronBRun | null): Record<string, number> => entryOf(run).counts || {};

const duration = (run: CronBRun | null) => {
  if (!run?.started_at || !run.completed_at) return '—';
  const s = Math.round((Date.parse(run.completed_at) - Date.parse(run.started_at)) / 1000);
  return s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}m` : `${(s / 3600).toFixed(1)}h`;
};

export const statusBadge = (status: string) => {
  const colors: Record<string, string> = {
    ok: 'bg-green-100 text-green-700',
    running: 'bg-blue-100 text-blue-700',
    partial: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    active: 'bg-green-100 text-green-700',
    reverted: 'bg-slate-200 text-slate-700',
    superseded: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const notScheduled = (
  <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500"
        title="No run of this stage in the last 7 days">
    Not scheduled
  </span>
);

interface Column {
  header: string;
  align?: 'right' | 'center';
  title?: string;
  cell: (r: CronBLatestRoute) => React.ReactNode;
  tone?: string;
}

// Columns after Route / Status / Last run, per stage (plan §3.6).
const STAGE_COLUMNS: Record<CronBStage, Column[]> = {
  export: [
    { header: 'Snapshot', cell: r => String(entryOf(r.run)['snapshot_id'] ?? '—') },
    { header: 'Signal rows', align: 'right', cell: r => fmt(r.run?.total_selected) },
    { header: 'Duration', align: 'right', cell: r => duration(r.run) },
  ],
  build: [
    { header: 'Built', align: 'right', cell: r => fmt(r.run?.total_selected) },
    { header: 'Better', align: 'right', tone: 'text-green-700', cell: r => fmt(r.run?.total_promoted) },
    { header: 'Same', align: 'right', cell: r => fmt(r.run?.total_equivalent) },
    { header: 'Worse', align: 'right', tone: 'text-amber-700', cell: r => fmt(r.run?.figures?.worse) },
    { header: 'Other holds', align: 'right', tone: 'text-amber-700', title: 'Held by the build itself (pages incomplete, bad slice, source mismatch, stale signal)', cell: r => fmt(r.run?.total_held) },
    { header: 'Errors', align: 'right', tone: 'text-red-700', cell: r => fmt(r.run?.total_failed) },
    { header: 'Synced at', title: 'When the merge job copied this build row from the buffer', cell: r => fmtTime(r.run?.synced_at) },
  ],
  merge: [
    { header: 'Merged', align: 'right', tone: 'text-green-700', cell: r => fmt(r.run?.total_promoted) },
    { header: 'Held', align: 'right', tone: 'text-amber-700', cell: r => fmt(r.run?.total_held) },
    { header: 'Errors', align: 'right', tone: 'text-red-700', cell: r => fmt(r.run?.total_failed) },
    { header: 'Backlog', align: 'right', title: 'Better candidates still waiting to merge (latest snapshot)', cell: r => fmt(r.backlog) },
  ],
  revert: [
    { header: 'Reverted', align: 'right', cell: r => fmt(r.run?.total_promoted) },
    { header: 'Refused', align: 'right', tone: 'text-amber-700', title: 'Revert refused, nothing changed', cell: r => fmt(r.run?.total_held) },
    {
      header: 'Merged docs (active / reverted / superseded)', align: 'right',
      cell: r => (r.merged_docs ? `${r.merged_docs.active} / ${r.merged_docs.reverted} / ${r.merged_docs.superseded}` : '—'),
    },
  ],
};

const CountsTable: React.FC<{ counts: Record<string, number>; caption: string }> = ({ counts, caption }) => {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <p className="text-xs text-slate-400">No {caption.toLowerCase()} recorded.</p>;
  return (
    <table className="text-xs" aria-label={caption}>
      <caption className="text-left font-medium text-slate-600 pb-1">{caption}</caption>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="pr-6 py-0.5 font-mono text-slate-600">{k}</td>
            <td className="py-0.5 text-right font-mono">{v.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Expanded route: latest run's breakdown + its recent runs (+ merged docs on Revert).
const RouteDetail: React.FC<{ stage: CronBStage; row: CronBLatestRoute }> = ({ stage, row }) => {
  const [runs, setRuns] = useState<CronBRun[] | null>(null);
  const [merged, setMerged] = useState<{ rows: MergedDoc[]; table_missing: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const route = encodeURIComponent(row.route);
    authApiClient.get(`/admin/reconcile/runs?stage=${stage}&route=${route}&limit=10`)
      .then(res => { if (!cancelled) setRuns(res.data.runs || []); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load runs'); });
    if (stage === 'revert') {
      authApiClient.get(`/admin/reconcile/merged?route=${route}&days=90`)
        .then(res => { if (!cancelled) setMerged(res.data); })
        .catch(() => { if (!cancelled) setMerged({ rows: [], table_missing: false }); });
    }
    return () => { cancelled = true; };
  }, [stage, row.route]);

  const entry = entryOf(row.run);
  const counts = countsOf(row.run);
  const facts = (['snapshot_id', 'signal_snapshot_id', 'target_build_id', 'metrics_version',
    'stop_reason', 'outcome', 'document_id', 'rollback_id'] as const)
    .filter(k => entry[k] !== undefined && entry[k] !== null);
  const sync = entry['sync'] as Record<string, unknown> | undefined;

  return (
    <div className="space-y-4" data-testid={`cron-b-detail-${row.route}`}>
      {row.run ? (
        <div className="flex flex-wrap gap-8">
          {stage === 'merge'
            ? <CountsTable counts={row.run.figures?.held_by_reason ?? {}} caption="Held by reason" />
            : null}
          <CountsTable counts={counts} caption={stage === 'build' ? 'Verdicts' : 'All outcomes'} />
          {facts.length > 0 && (
            <dl className="text-xs grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
              {facts.map(k => (
                <React.Fragment key={k}>
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-mono text-slate-700 break-all">{String(entry[k])}</dd>
                </React.Fragment>
              ))}
              {sync && (
                <>
                  <dt className="text-slate-500">buffer sync</dt>
                  <dd className="font-mono text-slate-700">{JSON.stringify(sync)}</dd>
                </>
              )}
            </dl>
          )}
          {row.run.reason && <p className="text-xs text-red-700">Reason: {row.run.reason}</p>}
        </div>
      ) : (
        <p className="text-xs text-slate-400">No {stage} run recorded for this route yet.</p>
      )}

      <div>
        <h4 className="text-xs font-medium text-slate-600 mb-1">Recent runs</h4>
        {error && <p className="text-xs text-red-600">{error}</p>}
        {!error && runs === null && <p className="text-xs text-slate-400">Loading…</p>}
        {runs && runs.length === 0 && <p className="text-xs text-slate-400">No runs.</p>}
        {runs && runs.length > 0 && (
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left py-1 font-medium">Started</th>
                <th className="text-left py-1 font-medium">Status</th>
                <th className="text-right py-1 font-medium">Selected</th>
                <th className="text-right py-1 font-medium">{stage === 'build' ? 'Better' : stage === 'revert' ? 'Reverted' : 'Promoted'}</th>
                <th className="text-right py-1 font-medium">{stage === 'build' ? 'Other holds' : stage === 'revert' ? 'Refused' : 'Held'}</th>
                <th className="text-right py-1 font-medium">Failed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map(r => (
                <tr key={r.run_id} title={r.run_id}>
                  <td className="py-1">{fmtTime(r.started_at)}</td>
                  <td className="py-1">{statusBadge(r.status)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.total_selected)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.total_promoted)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.total_held)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.total_failed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {stage === 'revert' && (
        <div>
          <h4 className="text-xs font-medium text-slate-600 mb-1">Merged documents (last 90 days)</h4>
          {merged === null && <p className="text-xs text-slate-400">Loading…</p>}
          {merged?.table_missing && (
            <p className="text-xs text-slate-500">Not deployed in this environment yet.</p>
          )}
          {merged && !merged.table_missing && merged.rows.length === 0 && (
            <p className="text-xs text-slate-400">No merged documents.</p>
          )}
          {merged && merged.rows.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1 font-medium">Document</th>
                  <th className="text-left py-1 font-medium">Key</th>
                  <th className="text-left py-1 font-medium">Merged at</th>
                  <th className="text-left py-1 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {merged.rows.map(d => (
                  <tr key={d.rollback_id}>
                    <td className="py-1 font-mono">{d.document_id}</td>
                    <td className="py-1 font-mono">{Object.values(d.doc_key || {}).join(' · ')}</td>
                    <td className="py-1">{fmtTime(d.merged_at)}</td>
                    <td className="py-1">{statusBadge(d.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

/** Latest view for one Cron B stage: one row per route, newest apply run.
 *  Polls every 10 s (same cadence as the old board) while mounted. */
export const CronBStageTable: React.FC<{ stage: CronBStage; pollMs?: number }> = ({ stage, pollMs = 10000 }) => {
  const [data, setData] = useState<CronBLatestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback((silent: boolean) => authApiClient
    .get<CronBLatestResponse>(`/admin/reconcile/latest?stage=${stage}`)
    .then(res => { setData(res.data); setError(null); })
    .catch(e => { if (!silent) setError(e?.response?.data?.detail || 'Failed to load Cron B runs'); }),
  [stage]);

  useEffect(() => {
    setData(null);
    setExpanded(null);
    load(false);
    if (!pollMs) return undefined;
    const id = setInterval(() => load(true), pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  const columns = STAGE_COLUMNS[stage];
  const span = 3 + columns.length;
  const isBad = (s?: string) => s === 'partial' || s === 'failed';

  return (
    <div>
      {stage === 'build' && data && (
        <p className="text-xs text-slate-500 mb-2" data-testid="cron-b-last-synced">
          Build rows come from the backfill VM and reach this view when a merge runs.
          Last synced: {fmtTime(data.last_synced_at)}
        </p>
      )}
      {stage === 'revert' && data?.merged_table_missing && (
        <p className="text-xs text-slate-500 mb-2">Merged-docs table not deployed in this environment yet.</p>
      )}
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Route</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Last run</th>
              {columns.map(c => (
                <th key={c.header} title={c.title}
                    className={`px-4 py-3 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!data && !error && (
              <tr><td colSpan={span} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {error && (
              <tr><td colSpan={span} className="px-4 py-8 text-center text-red-600">{error}</td></tr>
            )}
            {data && data.routes.map(r => (
              <React.Fragment key={r.route}>
                <tr
                  data-testid={`cron-b-row-${r.route}`}
                  className={`hover:bg-slate-50 cursor-pointer ${isBad(r.run?.status) ? 'bg-amber-50/40' : ''} ${r.run ? '' : 'text-slate-400'}`}
                  onClick={() => setExpanded(expanded === r.route ? null : r.route)}
                  aria-expanded={expanded === r.route}
                >
                  <td className="px-4 py-3 text-slate-700" title={r.route}>
                    {cronBFamilyLabel(r.route)}{!r.scheduled && notScheduled}
                  </td>
                  <td className="px-4 py-3">{r.run ? statusBadge(r.run.status) : '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{fmtTime(r.run?.started_at)}</td>
                  {columns.map(c => (
                    <td key={c.header}
                        className={`px-4 py-3 ${c.align === 'right' ? 'text-right font-mono' : ''} ${c.tone ?? ''}`}>
                      {c.cell(r)}
                    </td>
                  ))}
                </tr>
                {expanded === r.route && (
                  <tr>
                    <td colSpan={span} className="px-4 pb-4 bg-slate-50/50">
                      <RouteDetail stage={stage} row={r} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
